import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UploadModal = ({ isOpen, onClose, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [linkedShipment, setLinkedShipment] = useState('');
  const [tags, setTags] = useState('');

  const categoryOptions = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'bl', label: 'Bill of Lading' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'contract', label: 'Contract' },
    { value: 'inspection', label: 'Inspection Report' },
    { value: 'customs', label: 'Customs Document' },
    { value: 'other', label: 'Other' },
  ];

  const shipmentOptions = [
    { value: 'SHP-2024-001', label: 'SHP-2024-001 - MV Ocean Pride' },
    { value: 'SHP-2024-002', label: 'SHP-2024-002 - MV Pacific Star' },
    { value: 'SHP-2024-003', label: 'SHP-2024-003 - MV Global Trader' },
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList)?.map((file) => ({
      id: Math.random()?.toString(36)?.substr(2, 9),
      file,
      name: file?.name,
      size: file?.size,
      type: file?.name?.split('.')?.pop(),
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (id) => {
    setFiles((prev) => prev?.filter((file) => file?.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  const handleSubmit = () => {
    if (files?.length === 0) return;
    onUpload({
      files,
      category,
      linkedShipment,
      tags: tags?.split(',')?.map((tag) => tag?.trim())?.filter(Boolean),
    });
    setFiles([]);
    setCategory('');
    setLinkedShipment('');
    setTags('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Upload Documents</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-accent/10 transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Icon name="Upload" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-sm font-medium text-foreground mb-2">
              Drag and drop files here
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              or click to browse from your computer
            </p>
            <input
              type="file"
              multiple
              onChange={handleChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="outline" size="sm" asChild>
                <span>Browse Files</span>
              </Button>
            </label>
          </div>

          {files?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Selected Files ({files?.length})
              </h3>
              <div className="space-y-2">
                {files?.map((file) => (
                  <div
                    key={file?.id}
                    className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                  >
                    <Icon name="File" size={24} color="var(--color-muted-foreground)" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {file?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file?.size)} • {file?.type?.toUpperCase()}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFile(file?.id)}
                      className="p-1.5 rounded-md hover:bg-accent/10 transition-colors"
                      aria-label="Remove file"
                    >
                      <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 space-y-4">
            <Select
              label="Category"
              placeholder="Select document category"
              options={categoryOptions}
              value={category}
              onChange={setCategory}
              required
            />

            <Select
              label="Linked Shipment"
              placeholder="Link to shipment (optional)"
              options={shipmentOptions}
              value={linkedShipment}
              onChange={setLinkedShipment}
              searchable
              clearable
            />

            <Input
              label="Tags"
              type="text"
              placeholder="Enter tags separated by commas"
              value={tags}
              onChange={(e) => setTags(e?.target?.value)}
              description="e.g., urgent, final, approved"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={files?.length === 0 || !category}
            iconName="Upload"
            iconPosition="left"
          >
            Upload {files?.length > 0 && `(${files?.length})`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;